// ============================================================================
// pmtilesProtocol.ts — регистрация PMTiles-протокола для MapLibre (SSoT).
//
// PMTiles v3/v4 — «карта одним файлом»: тайлы читаются из архива по HTTP Range
// (или из OPFS без сети). Протокол регистрируется ГЛОБАЛЬНО один раз.
//
// Источники:
//   • удалённый архив  — `pmtiles://<url>` (MapLibre + FetchSource);
//   • архив в OPFS     — `opfs:<имя>` (скачан заранее; работает без сети).
//
// Никаких серверных полей: OPFS — существующее клиентское хранилище
// (lib/storage/opfs.ts), URL — настройка (lib/geo/mapConfig.ts).
// ============================================================================

import * as maplibregl from 'maplibre-gl';
import { PMTiles, Protocol, type RangeResponse, type Source } from 'pmtiles';
import { opfsGet } from '@/lib/storage/opfs';
import { createLogger } from '@/lib/logger';

const log = createLogger('geo:pmtiles');

/** Источник тайлов из OPFS (чтение архива без сети). */
class OpfsSource implements Source {
  private readonly name: string;

  constructor(name: string) {
    this.name = name;
  }

  getKey(): string {
    return `opfs:${this.name}`;
  }

  async getBytes(offset: number, length: number): Promise<RangeResponse> {
    const blob = await opfsGet(this.name);
    if (!blob) {
      throw new Error(`PMTiles-архив «${this.name}» не найден в OPFS`);
    }
    const data = await blob.slice(offset, offset + length).arrayBuffer();
    return { data };
  }
}

let protocol: Protocol | null = null;

/** Протокол PMTiles (создаётся один раз; повторные вызовы — тот же экземпляр). */
export function getPmtilesProtocol(): Protocol {
  if (!protocol) {
    protocol = new Protocol();
    maplibregl.addProtocol('pmtiles', protocol.tile);
  }
  return protocol;
}

/**
 * Разрешение URL архива в адрес для MapLibre.
 * `opfs:<имя>` → архив берётся из OPFS (регистрируется в протоколе);
 * иначе — удалённый архив по URL.
 */
export function resolvePmtilesSourceUrl(rawUrl: string): string {
  const p = getPmtilesProtocol();
  if (rawUrl.startsWith('opfs:')) {
    const name = rawUrl.slice('opfs:'.length);
    if (!name) throw new Error('Не указано имя PMTiles-архива в OPFS');
    const key = `opfs:${name}`;
    if (!p.get(key)) {
      p.add(new PMTiles(new OpfsSource(name)));
      log.info('PMTiles OPFS-архив подключён', { name });
    }
    return `pmtiles://${key}`;
  }
  return `pmtiles://${rawUrl}`;
}
